import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { colorBlue } from '@/utils/palette';
import { left } from '@/utils/directions';
import { getAuthSession } from '@/services/authService';
import SidebarLink, { SidebarLinkTitle } from './SidebarLink';
import SidebarCategory from './SidebarCategory';
import { buildMenuConfig } from './Config/menuConfig';

const findOpenMenus = (items, pathname, parents = []) => items.reduce((found, item) => {
  if (found) {
    return found;
  }

  if (item.type === 'link' && item.route === pathname) {
    return parents;
  }

  if (item.type === 'category' && item.children?.length) {
    return findOpenMenus(item.children, pathname, [...parents, item.key]);
  }

  return null;
}, null);

const renderMenuItems = ({
  items,
  onClick,
  collapse,
  openTopMenu,
  openConfigMenu,
  toggleTopMenu,
  toggleConfigMenu,
  level = 0,
}) => items.map((item) => {
    if (item.type === 'link') {
      return (
        <SidebarLink
          key={item.key}
          title={item.title}
          icon={item.icon}
          route={item.route}
          onClick={onClick}
        />
      );
    }

    const isTopLevel = level === 0;
    const isOpen = isTopLevel
      ? openTopMenu === item.key
      : openConfigMenu === item.key;

    const onToggle = isTopLevel
      ? () => toggleTopMenu(item.key)
      : () => toggleConfigMenu(item.key);

    return (
      <SidebarCategory
        key={item.key}
        title={item.title}
        icon={item.icon}
        collapse={collapse}
        animate={item.animate}
        isOpen={isOpen}
        onToggle={onToggle}
      >
        {renderMenuItems({
          items: item.children || [],
          onClick,
          collapse,
          openTopMenu,
          openConfigMenu,
          toggleTopMenu,
          toggleConfigMenu,
          level: level + 1,
        })}
      </SidebarCategory>
    );
  });

const SidebarContent = ({ onClick, collapse }) => {
  const location = useLocation();
  const [openTopMenu, setOpenTopMenu] = useState(null);
  const [openConfigMenu, setOpenConfigMenu] = useState(null);
  const authSession = useMemo(() => getAuthSession(), []);
  const menuConfig = useMemo(
    () => buildMenuConfig(authSession?.menu || [], authSession),
    [authSession],
  );

  const activeMenus = useMemo(
    () => findOpenMenus(menuConfig, location.pathname) || [],
    [location.pathname, menuConfig],
  );

  useEffect(() => {
    if (collapse) {
      setOpenTopMenu(null);
      setOpenConfigMenu(null);
      return;
    }

    setOpenTopMenu(activeMenus[0] || null);
    setOpenConfigMenu(activeMenus[1] || null);
  }, [collapse, activeMenus]);

  const toggleTopMenu = (key) => {
    setOpenTopMenu((prev) => {
      const next = prev === key ? null : key;

      return next;
    });
  };

  const toggleConfigMenu = (key) => {
    setOpenConfigMenu(prev => (prev === key ? null : key));
  };

  const handleCollapsedMouseLeave = () => {
    if (!collapse) {
      return;
    }

    setOpenTopMenu(null);
    setOpenConfigMenu(null);
  };

  return (
    <SidebarContentWrap
      collapse={collapse}
      onMouseLeave={handleCollapsedMouseLeave}
    >
      <SidebarBlock collapse={collapse}>
        {renderMenuItems({
          items: menuConfig,
          onClick,
          collapse,
          openTopMenu,
          openConfigMenu,
          toggleTopMenu,
          toggleConfigMenu,
        })}
      </SidebarBlock>
    </SidebarContentWrap>
  );
};

SidebarContent.propTypes = {
  onClick: PropTypes.func.isRequired,
  collapse: PropTypes.bool,
};

SidebarContent.defaultProps = {
  collapse: false,
};

export default SidebarContent;

// region STYLES

const SidebarContentWrap = styled.div`
  height: 100%;
  overflow: auto;
  padding: 6px 10px 16px;

  & > div:last-child {
    width: 4px !important;

    div {
      transition: height 0.3s;
      opacity: 0.52;
    }
  }

  @media screen and (min-width: 576px) {
    padding: 8px 12px 20px;

    ${props => props.collapse && `
      width: 64px;
      overflow: visible !important;
      transition: width 0.3s;
      padding-left: 6px;
      padding-right: 6px;
    `}
  }
`;

const SidebarBlock = styled.ul`
  padding: 6px 0 12px;
  list-style-type: none;
  display: flex;
  flex-direction: column;
  gap: 6px;

  &:last-child {
    border: none;
  }

  @media screen and (min-width: 576px) {
    ${props => props.collapse && `
      & > li > a,
      & > li > button {
        overflow: hidden;
        width: 52px;
        background: transparent;
        box-shadow: none;
        z-index: 1;

        ${SidebarLinkTitle} {
          position: absolute;
          width: 190px;
          ${left(props)}: 72px;
          opacity: 0;
          transform: translateX(-8px);
          pointer-events: none;
          transition: opacity 0.18s ease, transform 0.18s ease;
          z-index: 2;
        }

        &:hover {
          background: rgba(255, 255, 255, 0.08);
        }
      }

      & > li > button[aria-expanded='true'] {
        width: 292px;
        background: ${colorBlue} !important;
        color: rgba(255, 255, 255, 0.88);
        box-shadow: 0 12px 26px rgba(10, 21, 37, 0.18);
        z-index: 3;

        ${SidebarLinkTitle} {
          opacity: 1;
          transform: translateX(0);
          transition-delay: 0.08s;
        }
      }

      & > li:hover > a,
      & > li:hover > button {
        width: 292px;
        background: ${colorBlue} !important;
        color: rgba(255, 255, 255, 0.88);
        box-shadow: 0 12px 26px rgba(10, 21, 37, 0.18);
        z-index: 3;

        ${SidebarLinkTitle} {
          opacity: 1;
          transform: translateX(0);
          transition-delay: 0.08s;
        }
      }
    `}
  }
`;

// endregion
